import type { GenericFormType } from '@neo/blocks/forms/types'
import { useMemo } from 'react'
import { extractFieldsAndSchemaFromAction } from 'v2/pages/flow/hooks/use-action-fields'
import { useTypedReactFlow } from 'v2/pages/flow/hooks/use-react-flow'
import { useMapActionParametersMutation } from 'v2/pages/flow/queries'
import { useFlowStore } from 'v2/pages/flow/store'
import { ActionFieldType, type AiActionNodeData } from 'v2/pages/flow/types'
import {
  getAiConversationNodeVarDescriptions,
  refreshNodeVarsFromAncestors,
} from 'v2/pages/flow/utils'
import { getActionDescription } from 'v2/pages/flow/utils/ai-action-utils'
import { isAiAgentV2Type } from 'v2/pages/flow/utils/ai-agent-v29'
import { useExternalOrgAppsQuery } from 'v2/pages/settings/integrations/queries'
import type { McpAction } from 'v2/pages/settings/types'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'

interface PreparePayloadParams {
  nodeVars: string[]
  aiConversationVars: Array<{ nodevar: string; description: string }>
  form: GenericFormType<AiActionNodeData['config']>
  arrayIndex: number
  activeApps: RExternalOrgApp[]
}

const prepareNodeVariablesPayload = ({
  nodeVars,
  aiConversationVars,
  form,
  arrayIndex,
  activeApps,
}: PreparePayloadParams) => {
  const aiConversationNodeVarNames = aiConversationVars.map((aiVar) => aiVar.nodevar)

  const nodeVariables = [
    ...nodeVars
      .filter((nodeVar) => !aiConversationNodeVarNames.includes(nodeVar))
      .map((nodeVar) => ({ name: nodeVar })),
    ...aiConversationVars.map((aiVar) => ({
      name: aiVar.nodevar,
      description: aiVar.description,
    })),
  ]

  const toolName = form.getFieldValue(`actions[${arrayIndex}].action_name`)
  const actionProvider = form.getFieldValue(`actions[${arrayIndex}].app_provider`)
  const appId = form.getFieldValue(`actions[${arrayIndex}].org_app_uuid`)

  const actionDescription = getActionDescription({
    actionName: toolName || '',
    appId,
    activeApps,
  })

  // Get current action schema
  const currentActionName = form.getFieldValue(`actions[${arrayIndex}].action_name`)
  const { schema: actionSchema } = extractFieldsAndSchemaFromAction(
    currentActionName,
    activeApps,
    appId,
  )

  if (!actionSchema) return null

  return {
    node_variables: nodeVariables,
    action_schema: actionSchema as McpAction['input_schema'],
    tool_schema: {
      name: actionProvider || '',
      ...(actionDescription && { description: actionDescription }),
      ...(toolName && { action: toolName }),
    },
  }
}

// Update form fields from API mapping response
const updateFormFieldsFromMapping = (
  mappings: Record<string, string>,
  form: GenericFormType<AiActionNodeData['config']>,
  arrayIndex: number,
  optionalFields: string[],
) => {
  Object.entries(mappings).forEach(([fieldName, nodeVar]) => {
    // Get current field value to check if it should be preserved
    const currentValue = form.getFieldValue(`actions[${arrayIndex}].action_inputs.${fieldName}`)

    // Skip autofill if field has a manual value (doesn't start with {{ and not empty/null)
    if (
      currentValue &&
      typeof currentValue === 'string' &&
      currentValue.trim() &&
      !currentValue.startsWith('{{')
    ) {
      return // Don't overwrite manual values
    }

    // Set the field value
    form.setFieldValue(`actions[${arrayIndex}].action_inputs.${fieldName}`, nodeVar)
    form.validateField(`actions[${arrayIndex}].action_inputs.${fieldName}`, 'change')

    // Switch field to manual mode since we have a mapped value
    const actionFieldTypes = form.getFieldValue(`actions[${arrayIndex}].action_field_types`) || {
      required_fields: {},
      optional_fields: {},
    }

    const isOptionalField = optionalFields.includes(fieldName)
    if (isOptionalField) {
      actionFieldTypes.optional_fields = actionFieldTypes.optional_fields || {}
      actionFieldTypes.optional_fields[fieldName] = ActionFieldType.MANUAL
    } else {
      actionFieldTypes.required_fields = actionFieldTypes.required_fields || {}
      actionFieldTypes.required_fields[fieldName] = ActionFieldType.MANUAL
    }

    form.setFieldValue(`actions[${arrayIndex}].action_field_types`, actionFieldTypes)
  })
}

// Custom hook for action autofill logic
export const useActionAutofill = (
  form: GenericFormType<AiActionNodeData['config']>,
  arrayIndex: number,
  optionalFields: string[],
  nodeVars: string[],
) => {
  const { getNodes } = useTypedReactFlow()
  const { mutate: mapActionParameters, isPending: isMappingParameters } =
    useMapActionParametersMutation()
  const { data: activeApps } = useExternalOrgAppsQuery(true)

  // Get AI conversation node variable descriptions for better matching
  const aiConversationVars = useMemo(() => {
    const nodes = getNodes()
    const aiConversationNodes = nodes.filter((node) => isAiAgentV2Type(node.type))

    const descriptions = aiConversationNodes.flatMap((node) =>
      getAiConversationNodeVarDescriptions(node),
    )
    return descriptions
  }, [getNodes])

  const handleRefreshNodeVars = () => {
    const { selectedNodeID } = useFlowStore.getState()
    if (!selectedNodeID || isMappingParameters) return

    refreshNodeVarsFromAncestors(selectedNodeID)

    const payload = prepareNodeVariablesPayload({
      nodeVars,
      aiConversationVars,
      form,
      arrayIndex,
      activeApps: activeApps || [],
    })

    if (!payload) return

    mapActionParameters(payload, {
      onSuccess: (response) => {
        const mappings = response.mapping || {}
        updateFormFieldsFromMapping(mappings, form, arrayIndex, optionalFields)
      },
      onError: (error) => {
        logger.error('Failed to map action parameters:', error)
      },
    })
  }

  return {
    handleRefreshNodeVars,
    isMappingParameters,
  }
}
